import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';

/**
 * WHAT ONE OUTGOING MESSAGE COSTS THIS CLINIC.
 *
 * Quoted by the server through the same markup → FX → ceil pipeline a real
 * debit is priced with (`MessagingRateService.quoteUnitRate`), keyed to the
 * clinic's own country — a WhatsApp message to Egypt and one to Saudi Arabia
 * are not the same price, and a hub that printed one figure for both would be
 * wrong for whichever clinic is reading it.
 */
export interface MessagePrices {
  currency: string;
  country: string;
  whatsappMinor: number;
  smsMinor: number;
  inboundFree: boolean;
}

export function useMessagePrices(currency: string | null) {
  const q = useQuery({
    queryKey: ['cm-message-prices', currency ?? 'egp'],
    staleTime: 600_000,
    retry: false,
    queryFn: async (): Promise<MessagePrices | null> => {
      const res = await apiClient.get('/clinic-settings/message-prices', {
        params: { currency: (currency ?? 'egp').toLowerCase() },
      });
      return (res.data?.data ?? res.data ?? null) as MessagePrices | null;
    },
  });
  return { ...q, prices: q.data ?? null };
}

/** The payment providers this deployment can actually charge through. */
export interface ProviderRow {
  id: string;
  available: boolean;
  supportedCurrencies: string[];
  defaultCurrency: string;
}

export function usePaymentProviders() {
  const q = useQuery({
    queryKey: ['cm-payment-providers'],
    staleTime: 600_000,
    retry: false,
    queryFn: async (): Promise<ProviderRow[]> => {
      const res = await apiClient.get('/me/billing/providers');
      return (res.data?.data ?? res.data ?? []) as ProviderRow[];
    },
  });
  return { ...q, providers: q.data ?? [] };
}

/**
 * The one-off top-up. `POST /me/wallet/topup` charges the tenant's saved card
 * and credits only on success — so with no card on file it answers 402 with
 * the decline reason, which is what the sheet shows. Nothing is credited
 * optimistically and nothing is faked.
 */
export function useTopUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (amountMinor: number) => {
      const res = await apiClient.post('/me/wallet/topup', { amountMinor });
      return res.data?.data ?? res.data;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['cm-wallet'] }); },
  });
}

/** Which role holds which wallet verb — read from the roles' real permissions. */
export type WalletVerb = 'FULL' | 'READ' | 'NONE';

export function useWalletAccess() {
  const q = useQuery({
    queryKey: ['cm-wallet-access'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<Array<{ role: string; verb: WalletVerb }>> => {
      const res = await apiClient.get('/me/wallet/access');
      return (res.data?.data ?? res.data ?? []) as Array<{ role: string; verb: WalletVerb }>;
    },
  });
  return { ...q, matrix: q.data ?? null };
}
